import React from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Shield, Lock, Eye, Database, UserCheck } from 'lucide-react';

const PrivacyPolicy = () => {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <div className="bg-slate-800 text-white py-6">
        <div className="max-w-4xl mx-auto px-4">
          <button
            onClick={() => navigate('/')}
            className="flex items-center gap-2 text-[#1F6FD6] hover:text-[#E3EDF9] transition-colors mb-4"
          >
            <ArrowLeft size={20} />
            Retour à l'accueil
          </button>
          <h1 className="text-3xl font-bold">Politique de Confidentialité</h1>
          <p className="text-slate-300 mt-2">Conforme au RGPD (Règlement Général sur la Protection des Données)</p>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-4xl mx-auto px-4 py-12">
        <div className="bg-white rounded-xl shadow-lg p-8 space-y-8">
          
          {/* Introduction */}
          <section>
            <div className="flex items-center gap-3 mb-4">
              <Shield className="text-[#1F6FD6]" size={32} />
              <h2 className="text-2xl font-bold text-slate-800">
                Protection de vos données personnelles
              </h2>
            </div>
            <p className="text-slate-700 leading-relaxed">
              SMA CONCEPT accorde une grande importance à la protection de vos données personnelles. 
              Cette politique de confidentialité explique comment nous collectons, utilisons et protégeons 
              vos informations conformément au RGPD.
            </p>
          </section>

          {/* Responsable du traitement */}
          <section>
            <div className="flex items-center gap-3 mb-4">
              <UserCheck className="text-[#1F6FD6]" size={28} />
              <h2 className="text-2xl font-bold text-slate-800">
                Responsable du traitement
              </h2>
            </div>
            <div className="bg-slate-50 p-6 rounded-lg space-y-2">
              <p><strong>Entreprise :</strong> SMA CONCEPT</p>
              <p><strong>Email :</strong> contact@sma-concept.fr</p>
              <p><strong>Adresse :</strong> Saint-Étienne, France</p>
            </div>
          </section>

          {/* Données collectées */}
          <section>
            <div className="flex items-center gap-3 mb-4">
              <Database className="text-[#1F6FD6]" size={28} />
              <h2 className="text-2xl font-bold text-slate-800">
                Données collectées
              </h2>
            </div>
            <div className="space-y-4">
              <p className="text-slate-700">
                Nous collectons uniquement les données nécessaires au traitement de votre demande :
              </p>
              <ul className="space-y-2">
                <li className="flex items-start gap-2">
                  <span className="text-[#1F6FD6] mt-1">•</span>
                  <span><strong>Nom et prénom</strong> : pour vous identifier</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-[#1F6FD6] mt-1">•</span>
                  <span><strong>Adresse email</strong> : pour vous envoyer notre réponse</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-[#1F6FD6] mt-1">•</span>
                  <span><strong>Numéro de téléphone</strong> : pour vous contacter rapidement</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-[#1F6FD6] mt-1">•</span>
                  <span><strong>Type de service</strong> : pour orienter votre demande</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-[#1F6FD6] mt-1">•</span>
                  <span><strong>Message</strong> : pour comprendre votre projet</span>
                </li>
              </ul>
            </div>
          </section>

          {/* Finalité */}
          <section>
            <div className="flex items-center gap-3 mb-4">
              <Eye className="text-[#1F6FD6]" size={28} />
              <h2 className="text-2xl font-bold text-slate-800">
                Utilisation de vos données
              </h2>
            </div>
            <div className="space-y-3 text-slate-700">
              <p><strong>Vos données sont utilisées exclusivement pour :</strong></p>
              <ul className="space-y-2 ml-4">
                <li>✓ Traiter votre demande de devis</li>
                <li>✓ Vous recontacter pour établir un devis personnalisé</li>
                <li>✓ Assurer le suivi de votre dossier</li>
                <li>✓ Améliorer nos services</li>
              </ul>
              <div className="bg-green-50 border-l-4 border-green-500 p-4 mt-4">
                <p className="text-green-800 font-semibold">
                  ✓ Vos données ne sont JAMAIS vendues ni utilisées à des fins publicitaires
                </p>
              </div>
              <h3 className="font-bold text-slate-800 mt-6">Sous-traitants techniques</h3>
              <p>
                Pour assurer le fonctionnement du site, vos données transitent uniquement par les prestataires suivants,
                agissant sur nos instructions et conformément au RGPD :
              </p>
              <ul className="space-y-2 ml-4">
                <li>• <strong>Brevo</strong> (Sendinblue SAS, Paris, France) : acheminement de l'email de notification de votre demande de devis.</li>
                <li>• <strong>Emergent Technologies</strong> : hébergement du site et de la base de données.</li>
              </ul>
            </div>
          </section>

          {/* Base légale */}
          <section>
            <h2 className="text-2xl font-bold text-slate-800 mb-4 border-b-2 border-[#1F6FD6] pb-2">
              Base légale du traitement
            </h2>
            <p className="text-slate-700">
              Le traitement de vos données repose sur votre <strong>consentement explicite</strong> lors 
              de l'envoi du formulaire de contact. Vous pouvez retirer ce consentement à tout moment.
            </p>
          </section>

          {/* Conservation */}
          <section>
            <div className="flex items-center gap-3 mb-4">
              <Lock className="text-[#1F6FD6]" size={28} />
              <h2 className="text-2xl font-bold text-slate-800">
                Durée de conservation
              </h2>
            </div>
            <div className="space-y-3 text-slate-700">
              <p>
                <strong>Demandes de devis :</strong> 3 ans à compter de votre dernière interaction
              </p>
              <p>
                <strong>Clients :</strong> Pendant la durée de la relation commerciale + durées légales d'archivage
              </p>
              <p className="text-sm text-slate-600 italic mt-4">
                Les demandes de devis de plus de 3 ans sont automatiquement purgées de notre base de données.
              </p>
            </div>
          </section>

          {/* Sécurité */}
          <section>
            <h2 className="text-2xl font-bold text-slate-800 mb-4 border-b-2 border-[#1F6FD6] pb-2">
              Sécurité de vos données
            </h2>
            <div className="space-y-3 text-slate-700">
              <p>
                Nous mettons en œuvre toutes les mesures techniques et organisationnelles appropriées 
                pour protéger vos données contre :
              </p>
              <ul className="space-y-2 ml-4">
                <li>• La perte accidentelle</li>
                <li>• L'accès non autorisé</li>
                <li>• La modification ou la divulgation</li>
                <li>• La destruction</li>
              </ul>
              <div className="bg-[#E3EDF9] border-l-4 border-[#1F6FD6] p-4 mt-4">
                <p className="text-[#01162D]">
                  <strong>🔒 Connexion sécurisée HTTPS</strong> - Toutes les données transitent via un canal crypté
                </p>
              </div>
            </div>
          </section>

          {/* Vos droits */}
          <section>
            <h2 className="text-2xl font-bold text-slate-800 mb-4 border-b-2 border-[#1F6FD6] pb-2">
              Vos droits (RGPD)
            </h2>
            <div className="space-y-4">
              <p className="text-slate-700">
                Conformément au RGPD, vous disposez des droits suivants sur vos données personnelles :
              </p>
              <div className="grid md:grid-cols-2 gap-4">
                <div className="bg-slate-50 p-4 rounded-lg">
                  <h3 className="font-bold text-slate-800 mb-2">✓ Droit d'accès</h3>
                  <p className="text-sm text-slate-600">Connaître les données que nous détenons sur vous</p>
                </div>
                <div className="bg-slate-50 p-4 rounded-lg">
                  <h3 className="font-bold text-slate-800 mb-2">✓ Droit de rectification</h3>
                  <p className="text-sm text-slate-600">Corriger des données inexactes</p>
                </div>
                <div className="bg-slate-50 p-4 rounded-lg">
                  <h3 className="font-bold text-slate-800 mb-2">✓ Droit à l'effacement</h3>
                  <p className="text-sm text-slate-600">Supprimer vos données (droit à l'oubli)</p>
                </div>
                <div className="bg-slate-50 p-4 rounded-lg">
                  <h3 className="font-bold text-slate-800 mb-2">✓ Droit d'opposition</h3>
                  <p className="text-sm text-slate-600">Vous opposer au traitement de vos données</p>
                </div>
                <div className="bg-slate-50 p-4 rounded-lg">
                  <h3 className="font-bold text-slate-800 mb-2">✓ Droit à la portabilité</h3>
                  <p className="text-sm text-slate-600">Récupérer vos données dans un format exploitable</p>
                </div>
                <div className="bg-slate-50 p-4 rounded-lg">
                  <h3 className="font-bold text-slate-800 mb-2">✓ Droit de limitation</h3>
                  <p className="text-sm text-slate-600">Limiter le traitement de vos données</p>
                </div>
              </div>
            </div>
          </section>

          {/* Exercer vos droits */}
          <section className="bg-blue-50 border-2 border-[#1F6FD6] rounded-lg p-6">
            <h2 className="text-2xl font-bold text-slate-800 mb-4">
              Comment exercer vos droits ?
            </h2>
            <p className="text-slate-700 mb-4">
              Pour toute demande concernant vos données personnelles, contactez-nous :
            </p>
            <div className="space-y-2">
              <p className="text-slate-800">
                <strong>📧 Email :</strong> <a href="mailto:contact@sma-concept.fr" className="text-[#1F6FD6] hover:underline">contact@sma-concept.fr</a>
              </p>
              <p className="text-sm text-slate-600 mt-4">
                Nous nous engageons à répondre à votre demande dans un délai maximum d'<strong>1 mois</strong>.
              </p>
            </div>
          </section>

          {/* Réclamation */}
          <section>
            <h2 className="text-2xl font-bold text-slate-800 mb-4 border-b-2 border-[#1F6FD6] pb-2">
              Droit de réclamation
            </h2>
            <p className="text-slate-700">
              Si vous estimez que vos droits ne sont pas respectés, vous avez le droit d'introduire 
              une réclamation auprès de la CNIL (Commission Nationale de l'Informatique et des Libertés) :
            </p>
            <div className="bg-slate-50 p-4 rounded-lg mt-4">
              <p><strong>CNIL</strong></p>
              <p>3 Place de Fontenoy - TSA 80715</p>
              <p>75334 PARIS CEDEX 07</p>
              <p>Tel : 01 53 73 22 22</p>
              <p>Site web : <a href="https://www.cnil.fr" target="_blank" rel="noopener noreferrer" className="text-[#1F6FD6] hover:underline">www.cnil.fr</a></p>
            </div>
          </section>

          {/* Modifications */}
          <section>
            <h2 className="text-2xl font-bold text-slate-800 mb-4 border-b-2 border-[#1F6FD6] pb-2">
              Modifications de la politique
            </h2>
            <p className="text-slate-700">
              Cette politique de confidentialité peut être mise à jour. Toute modification sera publiée 
              sur cette page avec une nouvelle date de mise à jour.
            </p>
          </section>

          {/* Date de mise à jour */}
          <section className="pt-6 border-t border-slate-200">
            <p className="text-sm text-slate-500">
              <strong>Dernière mise à jour :</strong> Avril 2025
            </p>
            <p className="text-sm text-slate-500 mt-2">
              <strong>Version :</strong> 1.0
            </p>
          </section>

        </div>
      </div>
    </div>
  );
};

export default PrivacyPolicy;
